import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { formatDate, formatDateTime } from '@/lib/utils';
import { fetchOficinaBoard, fetchOficinaCompanies, type OficinaBoardOrder } from '@/services/oficinaApi';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Wrench, Maximize2, Minimize2 } from 'lucide-react';
import { useAutoVerticalScroll } from '@/hooks/useAutoVerticalScroll';
import { painelOficina } from '@/lib/painelOficina';
import { OficinaMercosulPlate } from '@/components/oficina/OficinaMercosulPlate';

const POLL_MS = 60_000;
/** 2× o default do hook (26 px/s) — rolagem automática das colunas do Kanban. */
const OFICINA_KANBAN_SCROLL_PX_PER_SEC = 52;

function OficinaKanbanColumnScroll({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useAutoVerticalScroll(ref, { pixelsPerSecond: OFICINA_KANBAN_SCROLL_PX_PER_SEC });
  return (
    <div
      ref={ref}
      className="min-h-0 flex-1 basis-0 space-y-3 overflow-y-auto overflow-x-hidden overscroll-y-contain p-3 [-webkit-overflow-scrolling:touch] [overflow-anchor:none]"
    >
      {children}
    </div>
  );
}

function veiculoLabel(o: OficinaBoardOrder): string {
  const parts = [o.veiculoMarca, o.veiculoModelo].filter(Boolean).join(' ');
  if (o.veiculoAnoFabricacao != null) {
    return parts ? `${parts} / ${o.veiculoAnoFabricacao}` : String(o.veiculoAnoFabricacao);
  }
  return parts || '—';
}

function isSaidaAtrasada(dataPrevistaSaida: string, now: Date): boolean {
  const prev = new Date(dataPrevistaSaida).getTime();
  if (Number.isNaN(prev)) return false;
  return prev < now.getTime();
}

export const DashboardOficinaPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(searchParams.get('companyId') ?? '');
  const [isFs, setIsFs] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-oficina-dashboard'],
    queryFn: fetchOficinaCompanies,
  });

  useEffect(() => {
    if (companies.length && !companyId) {
      setCompanyId(companies[0]!.id);
    }
  }, [companies, companyId]);

  useEffect(() => {
    navigate(painelOficina(companyId || null).kanbanLocation, { replace: true });
  }, [companyId, navigate]);

  const {
    data: boardPayload,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['oficina-board', companyId],
    queryFn: () => fetchOficinaBoard(companyId),
    enabled: Boolean(companyId),
    staleTime: 0,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const toggleFs = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().then(() => setIsFs(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFs(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFs = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const clockLabel = useMemo(() => format(now, 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }), [now]);

  const columns = boardPayload?.columns ?? [];

  const totalOrdensNoPainel = useMemo(
    () => columns.reduce((acc, col) => acc + col.ordens.length, 0),
    [columns]
  );

  const showTotalizadorPainel = Boolean(companyId) && !isLoading && !isError;

  return (
    <>
      <Helmet>
        <title>Dashboard Oficina | Cobranças</title>
      </Helmet>
      <div
        className="flex h-full min-h-0 max-h-full flex-1 flex-col overflow-hidden bg-background p-4 md:p-5 lg:p-6"
        data-testid="page-dashboard-oficina"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="grid shrink-0 grid-cols-1 items-center gap-4 lg:grid-cols-[1fr_auto_1fr] lg:gap-x-4 lg:gap-y-0">
            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:gap-4 lg:gap-6 justify-self-start">
              <h1 className="font-heading text-2xl font-semibold text-foreground md:text-3xl lg:text-4xl tracking-tight">
                Clientes em Atendimento
              </h1>
              {showTotalizadorPainel && (
                <div
                  className="flex shrink-0 flex-col items-start rounded-xl border border-border/70 bg-card/80 px-4 py-2.5 shadow-soft-sm backdrop-blur-sm sm:items-end sm:text-right sm:min-w-[180px]"
                  title="Total de ordens de serviço exibidas em todas as colunas do Kanban"
                >
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Total no painel
                  </span>
                  <span className="font-mono text-3xl font-bold tabular-nums leading-none text-foreground md:text-4xl">
                    {totalOrdensNoPainel}
                  </span>
                  <span className="text-xs text-muted-foreground">OS em todas as colunas</span>
                </div>
              )}
            </div>
            <div
              className="justify-self-center text-sm leading-none md:text-base"
              title="Data e hora atuais"
            >
              <div className="rounded-md border border-border/60 bg-card/70 px-4 py-3 font-mono text-[300%] tabular-nums text-foreground lg:px-6 lg:py-4">
                {clockLabel}
              </div>
            </div>
            <div className="flex w-full flex-col items-end gap-3 lg:w-auto lg:shrink-0 lg:justify-self-end">
              <div className="flex flex-wrap items-center justify-center gap-2.5 lg:justify-end">
                <div className="min-w-[220px] rounded-input border border-border/60 bg-card/60 p-1">
                  <Select
                    value={companyId}
                    onValueChange={setCompanyId}
                    placeholder="Selecione…"
                    className="text-base border-0 bg-transparent focus-visible:ring-0"
                  >
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <Button type="button" variant="outline" onClick={toggleFs} className="text-base border-border/70 bg-card/40">
                  {isFs ? <Minimize2 className="mr-2 h-5 w-5" /> : <Maximize2 className="mr-2 h-5 w-5" />}
                  {isFs ? 'Sair' : 'Tela cheia'}
                </Button>
              </div>
            </div>
          </div>

          {!companyId && (
            <p className="shrink-0 text-lg text-muted-foreground">Selecione uma empresa para carregar o painel.</p>
          )}

          {isError && (
            <p className="shrink-0 text-lg text-destructive">
              {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                'Erro ao carregar o painel.'}
            </p>
          )}

          {isLoading && companyId && (
            <div className="flex min-h-0 flex-1 shrink-0 items-center justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}

          {companyId && !isLoading && !isError && (
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
              <div className="flex min-h-0 flex-1 items-stretch gap-4 overflow-x-auto overflow-y-hidden pb-1 [overflow-anchor:none]">
                {columns.length === 0 ? (
                  <Card className="w-full max-w-none min-w-[280px] flex-1 border-dashed bg-card/60">
                    <CardContent className="flex h-full min-h-[300px] flex-col items-center justify-center gap-2 py-12 text-center">
                      <Wrench className="h-10 w-10 text-muted-foreground" />
                      <p className="text-lg text-muted-foreground">Nenhuma OS em execução nesta empresa.</p>
                    </CardContent>
                  </Card>
                ) : (
                  columns.map((col) => {
                    return (
                      <div
                        key={col.setor}
                        className="flex min-h-0 max-h-full min-w-[260px] flex-1 shrink-0 flex-col overflow-hidden rounded-2xl border border-border/70 bg-card/80 shadow-soft-sm backdrop-blur-sm sm:min-w-[280px] lg:min-w-[300px]"
                      >
                        <div className="sticky top-0 z-10 border-b border-border/70 bg-muted/50 px-4 py-3 backdrop-blur-sm">
                          <div className="flex items-start justify-between gap-3">
                            <h2 className="min-w-0 flex-1 truncate text-lg font-semibold text-foreground md:text-xl">
                              {col.setor}
                            </h2>
                            <div
                              className="flex shrink-0 flex-col items-end gap-0.5 text-right"
                              title={`${col.ordens.length} ordem(ns) de serviço neste setor`}
                            >
                              <Badge
                                variant="secondary"
                                className="px-3 py-1 font-mono text-base font-bold tabular-nums md:text-lg"
                              >
                                {col.ordens.length}
                              </Badge>
                              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                OS neste status
                              </span>
                            </div>
                          </div>
                        </div>
                        <OficinaKanbanColumnScroll>
                          {col.ordens.map((o) => {
                            const atrasada = isSaidaAtrasada(o.dataPrevistaSaida, now);
                            return (
                              <Card
                                key={o.id}
                                className="w-full max-w-none border border-border/70 bg-background/70 shadow-none transition-all duration-200 hover:-translate-y-0.5 hover:bg-background hover:shadow-md"
                              >
                                <CardContent className="flex w-full min-w-0 gap-3 p-4 text-[15px] leading-snug">
                                  <div className="min-w-0 flex-1 space-y-2">
                                    <div className="space-y-1">
                                      <span className="block truncate font-semibold text-[19.5px] leading-snug text-yellow-500 dark:text-yellow-400">
                                        {o.clienteNome}
                                      </span>
                                      <span
                                        className="inline-block rounded-md border border-border/70 bg-muted/40 px-2 py-0.5 font-mono text-xs font-semibold tabular-nums text-foreground"
                                        title="Número da locação (id_locacao)"
                                      >
                                        Loc. {o.numeroLocacao ?? '—'}
                                      </span>
                                    </div>
                                    <p className="text-foreground/90">
                                      <span className="text-muted-foreground">OS:</span> {o.osCodigoErp}
                                    </p>
                                    <p className="text-foreground/90">
                                      <span className="text-muted-foreground">Entrada:</span>{' '}
                                      {formatDate(o.dataEntrada)}
                                    </p>
                                    <p
                                      className={
                                        atrasada ? 'font-medium text-red-500' : 'text-foreground/90'
                                      }
                                    >
                                      <span className="text-muted-foreground">Previsão saída:</span>{' '}
                                      {formatDateTime(o.dataPrevistaSaida)}
                                    </p>
                                    <p className="text-foreground/90">
                                      <span className="text-muted-foreground">Veículo:</span> {veiculoLabel(o)}
                                      {o.corVeiculoTexto ? (
                                        <span className="text-muted-foreground"> · {o.corVeiculoTexto}</span>
                                      ) : null}
                                    </p>
                                    {o.tecnicosNomes ? (
                                      <p
                                        className="text-foreground/90"
                                        title="Técnicos (tecnicos_nomes / API)"
                                      >
                                        <span className="text-muted-foreground">Técnico:</span> {o.tecnicosNomes}
                                      </p>
                                    ) : null}
                                    <p className="text-foreground/90">
                                      <span className="text-muted-foreground">Dias na oficina:</span>{' '}
                                      <span className="font-semibold text-foreground">{o.diasDesdeEntrada}</span>
                                    </p>
                                  </div>
                                  <div className="flex shrink-0 items-center justify-end self-stretch">
                                    <OficinaMercosulPlate placa={o.veiculoPlaca} />
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </OficinaKanbanColumnScroll>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
